// Shared WebSocket reconnection utilities to ensure respectful reconnection patterns

export const createReconnectManager = () => {
  let reconnectAttempts = 0;
  let lastReconnectTime = 0;
  let reconnectTimeout = null;

  const resetAttempts = () => {
    reconnectAttempts = 0;
  };

  const getBackoffDelay = () => {
    // Exponential backoff with jitter
    reconnectAttempts++;
    const baseDelay = 1000; // 1 second
    const maxDelay = 30000; // 30 seconds max
    const exponentialDelay = baseDelay * Math.pow(2, Math.min(reconnectAttempts - 1, 5));
    const jitter = Math.random() * 1000; // 0-1 second random jitter
    return Math.min(exponentialDelay + jitter, maxDelay);
  };

  const canReconnectNow = (minDelayMs = 5000) => {
    const now = Date.now();
    const timeSinceLastReconnect = now - lastReconnectTime;
    return timeSinceLastReconnect >= minDelayMs;
  };

  const scheduleReconnect = (connectFn, delay = null) => {
    if (reconnectTimeout) {
      clearTimeout(reconnectTimeout);
    }

    const reconnectDelay = delay !== null ? delay : getBackoffDelay();
    lastReconnectTime = Date.now();
    
    reconnectTimeout = setTimeout(() => {
      connectFn();
    }, reconnectDelay);

    return reconnectTimeout;
  };

  const clearPendingReconnect = () => {
    if (reconnectTimeout) {
      clearTimeout(reconnectTimeout);
      reconnectTimeout = null;
    }
  };

  const handleVisibilityReconnect = (webSocketRef, connectFn, options = {}) => {
    const { minDelay = 5000, reconnectDelay = 1000 } = options;

    if (document.visibilityState === 'visible') {
      // Check rate limit
      if (!canReconnectNow(minDelay)) {
        return false;
      }

      clearPendingReconnect();

      // Check if WebSocket needs reconnection
      if (!webSocketRef.current || 
          webSocketRef.current.readyState === WebSocket.CLOSED ||
          webSocketRef.current.readyState === WebSocket.CLOSING) {
        
        // Add small random delay (1-2 seconds) to prevent thundering herd
        const delay = reconnectDelay + Math.random() * 1000;
        scheduleReconnect(connectFn, delay);
        return true;
      }
    }
    return false;
  };

  return {
    resetAttempts,
    getBackoffDelay,
    canReconnectNow,
    scheduleReconnect,
    clearPendingReconnect,
    handleVisibilityReconnect
  };
};